import { useQuery } from '@tanstack/react-query'
import { useMyInfo } from 'src/services/apollo/auth'
import { baseClient } from 'src/services/client'
import { useDebugSnapshotOverride } from './debug/store'
import type { UANewbiePackEntryPoint, UANewbiePackSnapshot } from './types'

export * from './types'

export interface UANewbiePackMarkAhaShownResult {
  recorded: boolean
  alreadyShown: boolean
  ahaShownAt: string | null
}

const QUERY_KEY = ['ua-newbie-pack', 'snapshot'] as const

export function isUANewbiePackEntryVisible(
  snapshot: UANewbiePackSnapshot | null | undefined,
  entryPoint: UANewbiePackEntryPoint,
) {
  if (!snapshot) return false
  return snapshot.visibleEntryPoints.includes(entryPoint)
}

export function hasUANewbiePackPurchasableTier(
  snapshot: UANewbiePackSnapshot | null | undefined,
) {
  if (!snapshot) return false
  return snapshot.tiers.some(tier => tier.purchasable && !tier.purchased)
}

export function getUANewbiePackTierByItemId(
  snapshot: UANewbiePackSnapshot | null | undefined,
  itemId: string | null | undefined,
  legacyItemId?: string | null | undefined,
) {
  if (!snapshot) return null
  const itemIds = [itemId, legacyItemId].filter(Boolean)
  if (itemIds.length === 0) return null
  return snapshot.tiers.find(tier => itemIds.includes(tier.itemId)) ?? null
}

export function useUANewbiePackSnapshot(args?: { enabled?: boolean }) {
  const { data: me } = useMyInfo()
  const debugSnapshot = useDebugSnapshotOverride()

  const query = useQuery({
    queryKey: [...QUERY_KEY, me?.id],
    enabled: Boolean(me?.id) && (args?.enabled ?? true),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    queryFn: async (): Promise<UANewbiePackSnapshot | null> => {
      const response = await baseClient.get<UANewbiePackSnapshot>(
        '/v2/payment-events/newbie-pack/snapshot',
      )
      if (response.status === 401) {
        return null
      }
      if (response.status < 200 || response.status >= 300) {
        throw new Error(
          `Failed to fetch newbie pack snapshot: ${response.status}`,
        )
      }
      return response.data
    },
  })

  if (debugSnapshot) {
    return { ...query, data: debugSnapshot }
  }
  return query
}

export async function markUANewbiePackAhaShown() {
  const response = await baseClient.post<UANewbiePackMarkAhaShownResult>(
    '/v2/payment-events/newbie-pack/aha-shown',
    {},
  )
  if (response.status === 401) {
    return null
  }
  if (response.status < 200 || response.status >= 300) {
    throw new Error(`Failed to mark newbie pack aha shown: ${response.status}`)
  }
  return response.data
}
